import { useMutation, useQueryClient } from '@tanstack/react-query';
import { 
  payrollRollbackService, 
  type IndividualRollbackOptions 
} from '@/services/payroll/payrollRollbackService';
import { payrollKeys } from './queryKeys';
import { useToast } from '@/hooks/use-toast';

export const useIndividualPayslipRollback = () => {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: (options: IndividualRollbackOptions) =>
      payrollRollbackService.rollbackIndividualPayslips(options),
    onSuccess: (result, variables) => {
      if (result.success) {
        const employeeCount = variables.payslipIds.length;
        const accrualMessage = result.reversedAccruals > 0 
          ? ` ${result.reversedAccruals} leave accrual(s) reversed.` 
          : '';
        
        let statusMessage = '';
        if (result.runStatus === 'rolled_back') {
          statusMessage = ' Run marked as rolled back (no payslips remaining).';
        } else {
          statusMessage = ` ${result.remainingPayslips} payslip(s) remaining in run.`;
        }

        toast({
          title: 'Payslips Rolled Back',
          description: `Successfully rolled back ${result.deletedPayslips} of ${employeeCount} selected payslip(s).${accrualMessage}${statusMessage}`,
        });

        // Invalidate all payroll-related queries
        queryClient.invalidateQueries({ queryKey: payrollKeys.all });
      } else {
        toast({
          title: 'Rollback Failed',
          description: result.error || 'Failed to rollback selected payslips',
          variant: 'destructive',
        });
      }
    },
    onError: (error: Error) => {
      toast({
        title: 'Rollback Failed',
        description: error.message || 'An unexpected error occurred',
        variant: 'destructive',
      });
    },
  });
};
